class LetoilePredictiveSearch {
  constructor(root = document, storage = letoileStorage()) {
    this.forms = letoileElements(root, '[data-predictive-search-form]').filter((form) => !form.dataset.predictiveSearchBound);
    if (!this.forms.length) return;

    this.storage = storage;
    this.storageKey = 'letoile:recent-searches';
    this.maxRecentQueries = 10;
    this.minimumQueryLength = 3;
    this.debounceDelay = 300;
    this.debounceTimers = new WeakMap();
    this.controllers = new WeakMap();
    this.requestIds = new WeakMap();
    this.bind();
  }

  bind() {
    this.forms.forEach((form) => {
      form.dataset.predictiveSearchBound = 'true';
      const input = form.querySelector('[data-predictive-search-input]');
      const results = form.querySelector('[data-predictive-search-results]');
      const clear = form.querySelector('[data-predictive-search-clear]');
      if (!input || !results) return;

      const syncValueState = () => {
        input.dataset.hasValue = input.value.length > 0 ? 'true' : 'false';
      };
      syncValueState();
      input.addEventListener('input', () => {
        syncValueState();
        this.schedule(input, results);
      });
      input.addEventListener('focus', () => {
        if (input.matches('[data-mobile-search-entry]') && window.matchMedia('(max-width: 989px)').matches) return;
        this.schedule(input, results);
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          results.hidden = true;
          input.setAttribute('aria-expanded', 'false');
        }
      });
      form.addEventListener('submit', () => this.saveRecentQuery(input.value));
      clear?.addEventListener('click', () => {
        this.cancel(results);
        input.value = '';
        syncValueState();
        results.setAttribute('aria-busy', 'false');
        input.focus();
        this.renderRecent(results, input);
      });
      results.addEventListener('click', (event) => {
        const suggestion = event.target.closest('[data-predictive-search-suggestion]');
        if (suggestion) {
          event.preventDefault();
          input.value = suggestion.dataset.predictiveSearchSuggestion || '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.focus();
          return;
        }

        const clearHistory = event.target.closest('[data-recent-search-clear]');
        if (clearHistory) {
          event.preventDefault();
          this.writeRecentQueries([]);
          this.hide(results, input);
          return;
        }

        const removeHistory = event.target.closest('[data-recent-search-remove]');
        if (removeHistory) {
          event.preventDefault();
          event.stopPropagation();
          const normalized = this.normalizeQuery(removeHistory.dataset.recentSearchRemove);
          this.writeRecentQueries(this.readRecentQueries().filter((query) => this.normalizeQuery(query) !== normalized));
          this.renderRecent(results, input);
          return;
        }

        const queryLink = event.target.closest('[data-recent-search-query], [data-predictive-search-all-results]');
        if (queryLink) {
          this.saveRecentQuery(queryLink.dataset.recentSearchQuery || input.value);
        }
      });
      document.addEventListener('click', (event) => {
        const mobileSurface = form.closest('[data-mobile-search-surface]');
        if (
          mobileSurface
          && !mobileSurface.hidden
          && event.target.closest?.('[data-mobile-search-entry]')
        ) {
          return;
        }
        if (!form.contains(event.target)) {
          this.hide(results, input);
          if (window.matchMedia('(max-width: 989px)').matches) input.blur();
        }
      });
    });
  }

  schedule(input, results) {
    const query = input.value.trim();
    this.cancel(results);

    if (query.length < this.minimumQueryLength) {
      results.setAttribute('aria-busy', 'false');
      if (query.length === 0) {
        this.renderRecent(results, input);
      } else {
        this.hide(results, input);
      }
      return;
    }

    const timer = window.setTimeout(() => this.search(query, results, input), this.debounceDelay);
    this.debounceTimers.set(results, timer);
  }

  cancel(results) {
    const timer = this.debounceTimers.get(results);
    if (timer) window.clearTimeout(timer);
    this.debounceTimers.delete(results);
    this.controllers.get(results)?.abort();
    this.controllers.delete(results);
  }

  hide(results, input) {
    results.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  }

  async search(query, results, input) {
    this.controllers.get(results)?.abort();
    const controller = new AbortController();
    this.controllers.set(results, controller);
    const requestId = (this.requestIds.get(results) || 0) + 1;
    this.requestIds.set(results, requestId);
    results.setAttribute('aria-busy', 'true');

    const localeRoot = window.Shopify?.routes?.root || '/';
    const searchParameters = `q=${encodeURIComponent(query)}&resources[type]=query,product,collection&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last`;
    const url = `${localeRoot}search/suggest.json?${searchParameters}`;

    try {
      let response = await fetch(url, { signal: controller.signal });
      if (!response.ok && localeRoot !== '/') {
        response = await fetch(`/search/suggest.json?${searchParameters}`, { signal: controller.signal });
      }
      if (!this.isLatest(results, requestId, input, query)) return;
      if (!response.ok) {
        this.renderEmpty(results, input, query);
        return;
      }
      let data = await response.json();
      let searchResults = data.resources?.results || {};

      if (localeRoot !== '/' && !this.hasResults(searchResults)) {
        response = await fetch(`/search/suggest.json?${searchParameters}`, { signal: controller.signal });
        if (response.ok) {
          data = await response.json();
          searchResults = data.resources?.results || {};
        }
      }

      if (!this.isLatest(results, requestId, input, query)) return;
      this.render(searchResults, results, input, query);
    } catch (error) {
      if (error.name !== 'AbortError' && this.isLatest(results, requestId, input, query)) {
        this.renderEmpty(results, input, query);
      }
    } finally {
      if (this.requestIds.get(results) === requestId) {
        results.setAttribute('aria-busy', 'false');
        this.controllers.delete(results);
      }
    }
  }

  isLatest(results, requestId, input, query) {
    return this.requestIds.get(results) === requestId && input.value.trim() === query;
  }

  normalizeQuery(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  }

  readRecentQueries() {
    if (!this.storage) return [];
    try {
      const queries = JSON.parse(this.storage.getItem(this.storageKey) || '[]');
      return Array.isArray(queries) ? queries.filter((query) => typeof query === 'string') : [];
    } catch (_error) {
      return [];
    }
  }

  writeRecentQueries(queries) {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(queries.slice(0, this.maxRecentQueries)));
    } catch (_error) {
      this.storage = null;
    }
  }

  saveRecentQuery(value) {
    const query = String(value || '').trim().replace(/\s+/g, ' ');
    const normalized = this.normalizeQuery(query);
    if (normalized.length < this.minimumQueryLength) return;

    const queries = this.readRecentQueries().filter((item) => this.normalizeQuery(item) !== normalized);
    this.writeRecentQueries([query, ...queries]);
  }

  renderRecent(results, input) {
    const queries = this.readRecentQueries();
    if (!queries.length) {
      this.hide(results, input);
      return;
    }

    const heading = results.getAttribute('data-recent-searches-label') || '';
    const clearLabel = results.getAttribute('data-recent-searches-clear-label') || '';
    const removeTemplate = results.getAttribute('data-recent-searches-remove-label') || '';

    results.innerHTML = `
      <section class="predictive-search__recent" data-recent-searches>
        <header class="predictive-search__recent-heading">
          <h2>${this.escapeHtml(heading)}</h2>
          <button type="button" data-recent-search-clear>${this.escapeHtml(clearLabel)}</button>
        </header>
        <ul class="predictive-search__recent-list">
          ${queries.map((query) => {
            const queryUrl = this.localizePath(`/search?q=${encodeURIComponent(query)}&type=product`);
            const removeLabel = removeTemplate.replace('[terms]', query);
            return `
              <li>
                <a href="${this.escapeAttribute(queryUrl)}" data-recent-search-query="${this.escapeAttribute(query)}">
                  <span class="predictive-search__recent-icon" aria-hidden="true"></span>
                  <span>${this.escapeHtml(query)}</span>
                </a>
                <button type="button" data-recent-search-remove="${this.escapeAttribute(query)}" aria-label="${this.escapeAttribute(removeLabel)}">×</button>
              </li>
            `;
          }).join('')}
        </ul>
      </section>
    `;
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  hasResults(data) {
    return ['queries', 'products', 'collections', 'pages', 'articles'].some((key) => Array.isArray(data[key]) && data[key].length > 0);
  }

  render(data, results, input, query) {
    const labels = {
      product: results.getAttribute('data-product-label') || '',
      collection: results.getAttribute('data-collection-label') || '',
      page: results.getAttribute('data-page-label') || '',
      article: results.getAttribute('data-article-label') || '',
      suggestions: results.getAttribute('data-suggestions-label') || '',
      categories: results.getAttribute('data-categories-label') || '',
      brands: results.getAttribute('data-brands-label') || '',
      brandPrefix: results.getAttribute('data-brand-prefix') || '',
      products: results.getAttribute('data-products-label') || '',
      allResults: results.getAttribute('data-all-results-label') || '',
    };
    const groups = [
      {
        key: 'suggestions',
        title: labels.suggestions,
        items: (data.queries || []).map((item) => ({
          ...item,
          title: item.text || item.title || query,
          type: labels.suggestions,
        })),
      },
      {
        key: 'categories',
        title: labels.categories,
        items: (data.collections || []).map((item) => ({ ...item, type: labels.collection })),
      },
      {
        key: 'brands',
        title: labels.brands,
        items: window.LetoilePredictiveSearchView.brandItems(data.products || [], query, labels),
      },
      {
        key: 'products',
        title: labels.products,
        items: (data.products || []).map((item) => ({ ...item, type: labels.product })),
      },
    ];
    const chipItems = groups[0].items.slice(0, 3);
    const populatedGroups = groups
      .map((group) => (
        group.key === 'suggestions'
          ? { ...group, items: group.items.slice(chipItems.length) }
          : group
      ))
      .filter((group) => group.items.length > 0);

    if (!populatedGroups.length && !chipItems.length) {
      this.renderEmpty(results, input, query);
      return;
    }

    const allResultsUrl = this.localizePath(`/search?q=${encodeURIComponent(query)}&type=product`);
    results.innerHTML = `
      <div class="predictive-search__groups">
        ${chipItems.length > 0 ? `
          <div class="predictive-search__chips" aria-label="${this.escapeAttribute(labels.suggestions)}">
            ${chipItems.map((item) => `
              <button
                class="predictive-search__chip"
                type="button"
                data-predictive-search-suggestion="${this.escapeAttribute(item.title)}"
              >${this.escapeHtml(item.title)}</button>
            `).join('')}
          </div>
        ` : ''}
        ${populatedGroups.map((group) => `
          <section class="predictive-search__group predictive-search__group--${group.key}">
            ${group.title ? `<h2 class="visually-hidden">${this.escapeHtml(group.title)}</h2>` : ''}
            <ul class="predictive-search__list" data-predictive-search-list>
              ${group.items.map((item) => window.LetoilePredictiveSearchView.itemTemplate(item, group.key, query)).join('')}
            </ul>
          </section>
        `).join('')}
        <a class="predictive-search__all-results" href="${this.escapeAttribute(allResultsUrl)}" data-predictive-search-all-results>
          ${this.escapeHtml(labels.allResults)}
        </a>
      </div>
    `;
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  renderEmpty(results, input, query) {
    if (results.closest('[data-mobile-search-surface]')) {
      const allResultsLabel = results.getAttribute('data-all-results-label') || '';
      const allResultsUrl = this.localizePath(`/search?q=${encodeURIComponent(query)}&type=product`);
      results.innerHTML = `
        <a class="predictive-search__all-results" href="${this.escapeAttribute(allResultsUrl)}" data-predictive-search-all-results>
          ${this.escapeHtml(allResultsLabel)}
        </a>
      `;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      return;
    }

    const emptyTemplate = results.getAttribute('data-empty-text') || '';
    const emptyText = emptyTemplate.replace('[terms]', this.escapeHtml(query));
    results.innerHTML = `<div class="empty-state" data-predictive-search-empty>${emptyText}</div>`;
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  localizePath(value) {
    const path = String(value || '');
    const localeRoot = window.Shopify?.routes?.root || '/';
    if (localeRoot === '/' || !path.startsWith('/') || path.startsWith(localeRoot)) return path;
    return `${localeRoot.replace(/\/$/, '')}${path}`;
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }
}

new LetoilePredictiveSearch();

document.addEventListener('shopify:section:load', (event) => new LetoilePredictiveSearch(event.target));
